import { get, send } from "./api.js";

/** A groove on a lesson note, and whether its picture has been made. */
export interface LessonGroove {
  id: string;
  title: string;
  query: string;
  /** Snapshot revision for refusing edits started before another tutor save. */
  version: string;
  /** Opens the snapshot attached to this lesson. */
  token: string;
  position: number;
  /** Null until a browser has drawn and uploaded one. */
  renderUrl: string | null;
  /** Only the unchanged, editable library snapshot may use its render endpoint. */
  canRender?: boolean;
}

export interface GroovePracticeInstruction {
  id: string;
  position: number;
  note: string;
  /** Tutor-set starting/target BPM. Null means use the groove's saved tempo. */
  targetTempo: number | null;
}

export async function listLessonGrooves(lessonId: string): Promise<{ grooves: LessonGroove[] }> {
  return get(`/grooves/lessons/${lessonId}/grooves`);
}

export async function attachGroove(lessonId: string, grooveId: string): Promise<void> {
  await send("POST", `/grooves/lessons/${lessonId}/grooves`, { grooveId });
}

export async function detachGroove(lessonId: string, grooveId: string): Promise<void> {
  await send("DELETE", `/grooves/lessons/${lessonId}/grooves/${grooveId}`);
}

/** Update this lesson's snapshot and practice, without changing the library groove. */
export async function updateLessonGroove(
  lessonId: string,
  grooveId: string,
  input: { title: string; query: string; version: string },
): Promise<{ groove: LessonGroove }> {
  return send("PATCH", `/grooves/lessons/${encodeURIComponent(lessonId)}/grooves/${encodeURIComponent(grooveId)}`, input);
}

export async function listGroovePracticeInstructions(
  lessonId: string,
  grooveId: string,
): Promise<{ instructions: GroovePracticeInstruction[] }> {
  return get(`/groove-practice/lessons/${lessonId}/grooves/${grooveId}/instructions`);
}

export async function addGroovePracticeInstruction(
  lessonId: string,
  grooveId: string,
  input: { note?: string; targetTempo?: number | null } = {},
): Promise<{ instruction: GroovePracticeInstruction }> {
  return send("POST", `/groove-practice/lessons/${lessonId}/grooves/${grooveId}/instructions`, input);
}

export async function updateGroovePracticeInstruction(
  lessonId: string,
  grooveId: string,
  instructionId: string,
  input: { note?: string; targetTempo?: number | null },
): Promise<{ instruction: GroovePracticeInstruction }> {
  return send(
    "PATCH",
    `/groove-practice/lessons/${lessonId}/grooves/${grooveId}/instructions/${instructionId}`,
    input,
  );
}

export async function deleteGroovePracticeInstruction(
  lessonId: string,
  grooveId: string,
  instructionId: string,
): Promise<void> {
  await send(
    "DELETE",
    `/groove-practice/lessons/${lessonId}/grooves/${grooveId}/instructions/${instructionId}`,
  );
}

/**
 * Send the picture the page has just drawn.
 *
 * Multipart, because that is a file. The server works out which pattern it is
 * from the groove's stored query rather than believing anything sent here.
 */
export async function uploadRender(grooveId: string, png: Blob): Promise<{ url: string }> {
  const form = new FormData();
  form.set("file", png, "groove.png");
  const response = await fetch(`/api/grooves/${grooveId}/render`, { method: "POST", body: form });
  if (!response.ok) throw new Error(`Could not save the notation (${response.status})`);
  return (await response.json()) as { url: string };
}
